import type { Article } from "@/types/content";

const authors = {
  maya: { name: "VibeWell Editorial Team", role: "Business Technology Experts", initials: "VW" },
  devan: { name: "Devan Rao", role: "Engineering Lead", initials: "DR" },
  iris: { name: "Iris Novak", role: "Product Strategist", initials: "IN" },
};




export const ARTICLE_CATEGORIES = [
    "Artificial Intelligence",
    "Digital Transformation",
    "Enterprise Software",
    "ERP",
    "CRM",
    "Workflow Automation"
];

/**
 * Blog articles — scalable, SEO-ready content architecture.
 * Adding a post = one entry here. Categories and tags are derived from the
 * data, so filtering and related-article logic scale automatically.
 */
export const articles: Article[] = [
  {
    slug: "choosing-the-right-erp-for-your-business",

    featured: true,

    title: "Choosing the Right ERP for Your Business",

    excerpt:
      "Selecting the right Enterprise Resource Planning (ERP) solution is one of the most important technology decisions an organisation can make. Learn how to evaluate ERP platforms, avoid costly implementation mistakes and maximise return on investment.",

    seoTitle:
      "Choosing the Right ERP for Your Business | VibeWell Software Solutions",

    seoDescription:
      "A practical guide to selecting the right ERP system, planning implementation and avoiding common mistakes that delay digital transformation.",

    category: "ERP",

    tags: [
      "ERP",
      "Enterprise Software",
      "Business Process",
      "Digital Transformation",
      "Automation"
    ],

    readingTime: "8 min read",

    date: "Jul 2026",

    publishedAt: "2026-07-20",

    author: authors.maya,

    // cover: "from-primary via-primary/80 to-secondary",
    cover: "from-[#1F6357] via-[#1F6357]/80 to-[#31906E]/70",

    body: [

      {
        paragraphs: [
          "As organisations grow, managing finance, procurement, inventory, human resources, customer information and operations through disconnected software becomes increasingly difficult. Data gets duplicated, reporting becomes unreliable and employees spend valuable time performing manual tasks instead of focusing on business growth.",
          "An Enterprise Resource Planning (ERP) system brings these business functions together into a single integrated platform, providing one source of truth across the organisation."
        ]
      },

      {
        heading: "When Does Your Business Need an ERP?",

        paragraphs: [
          "Many organisations delay ERP implementation because they believe it is only suitable for large enterprises. In reality, growing businesses often benefit the most because they eliminate inefficiencies before they become deeply embedded.",
          "If your organisation relies heavily on spreadsheets, duplicate data entry, disconnected software applications or manual reporting, it may already be time to evaluate an ERP solution."
        ]
      },

      {
        heading: "Key Features to Evaluate",

        paragraphs: [
          "Look beyond marketing brochures. Evaluate how well the ERP supports your actual business processes including finance, inventory, procurement, manufacturing, asset management, CRM, HR and reporting.",
          "A scalable ERP should also provide role-based security, workflow automation, approval processes, dashboards, API integration and mobile accessibility."
        ]
      },

      {
        heading: "Cloud vs On-Premise ERP",

        paragraphs: [
          "Cloud ERP reduces infrastructure costs, simplifies upgrades and provides remote accessibility. On-premise ERP offers greater infrastructure control and may be preferred in industries with strict regulatory or security requirements.",
          "The right choice depends on compliance obligations, existing IT infrastructure, internet reliability and long-term operational strategy."
        ]
      },

      {
        heading: "Common Implementation Mistakes",

        paragraphs: [
          "Many ERP failures are caused not by technology but by inadequate planning. Organisations often attempt to automate inefficient processes instead of improving them first.",
          "Lack of executive sponsorship, insufficient user training, unrealistic timelines and poor data migration planning are among the most common reasons ERP projects exceed budget and schedule."
        ]
      },

      {
        heading: "Conclusion",

        paragraphs: [
          "An ERP implementation is more than a software project—it is a business transformation initiative. Choosing the right implementation partner, investing in planning and involving stakeholders early significantly improves the likelihood of long-term success."
        ]
      }

    ],

    related: [
      "digital-transformation-roadmap",
      "crm-vs-erp-understanding-the-difference"
    ]

  },
  /* =============================================================================
   ARTICLE 2
  ============================================================================= */
  {
    slug: "how-ai-can-transform-business-operations",

    featured: true,

    title: "How AI Can Transform Business Operations",

    excerpt:
      "Artificial Intelligence is no longer limited to large technology companies. Discover practical ways organisations are using AI to automate repetitive work, improve decision making and increase productivity.",

    seoTitle:
      "How AI Can Transform Business Operations | VibeWell Software Solutions",

    seoDescription:
      "Explore practical business applications of Artificial Intelligence including AI Agents, workflow automation, document processing and predictive analytics.",

    category: "Artificial Intelligence",

    tags: [
      "AI",
      "AI Agents",
      "Automation",
      "Business Intelligence",
      "Digital Transformation"
    ],

    readingTime: "9 min read",

    date: "Jul 2026",

    publishedAt: "2026-07-18",

    author: authors.maya,

    cover: "from-indigo-500 to-secondary",

    body: [

      {
        paragraphs: [
          "Artificial Intelligence has rapidly evolved from an emerging technology into a practical business tool. Organisations across industries are using AI to automate routine work, analyse large volumes of information and support faster, more informed decision making.",
          "The greatest value of AI is not replacing employees—it is enabling them to spend more time on high-value activities while repetitive tasks are handled automatically."
        ]
      },

      {
        heading: "AI Agents",

        paragraphs: [
          "AI Agents can independently perform multi-step business processes such as analysing emails, generating reports, processing support requests, validating documents and coordinating workflows across multiple business systems.",
          "Unlike traditional automation, AI Agents can interpret natural language, reason about tasks and adapt to changing business requirements."
        ]
      },

      {
        heading: "Document Intelligence",

        paragraphs: [
          "Invoices, purchase orders, contracts, inspection reports and compliance documents often require significant manual effort. AI-powered document processing automatically extracts structured information from scanned documents while reducing human error."
        ]
      },

      {
        heading: "Workflow Automation",

        paragraphs: [
          "AI can intelligently route approvals, classify requests, prioritise incidents, recommend actions and trigger automated workflows without requiring constant human intervention."
        ]
      },

      {
        heading: "Predictive Analytics",

        paragraphs: [
          "By analysing historical operational data, AI helps organisations forecast demand, identify equipment failures, optimise inventory levels and improve resource planning."
        ]
      },

      {
        heading: "Conclusion",

        paragraphs: [
          "Successful AI adoption begins with clearly defined business problems rather than technology experimentation. Organisations that focus on measurable outcomes achieve faster ROI and higher user adoption."
        ]
      }

    ],

    related: [
      "digital-transformation-roadmap",
      "business-processes-you-should-automate-first"
    ]

  },

  /* =============================================================================
   ARTICLE 3
  ============================================================================= */

  {
    slug: "digital-transformation-roadmap",

    featured: true,

    title: "Digital Transformation Roadmap for Growing Organisations",

    excerpt:
      "Digital transformation is much more than implementing new software. Learn how successful organisations modernise people, processes and technology through a structured transformation roadmap.",

    seoTitle:
      "Digital Transformation Roadmap | VibeWell Software Solutions",

    seoDescription:
      "Learn the essential phases of a successful digital transformation initiative, from business assessment and technology selection to implementation and continuous improvement.",

    category: "Digital Transformation",

    tags: [
      "Digital Transformation",
      "Business Process",
      "Modernisation",
      "Enterprise Software",
      "Strategy"
    ],

    readingTime: "9 min read",

    date: "Jul 2026",

    publishedAt: "2026-07-15",

    author: authors.maya,

    cover: "from-[#3D5791] via-[#3D5791]/80 to-[#728FCE]/70",

    body: [

      {
        paragraphs: [
          "Digital transformation is often misunderstood as simply purchasing new software. In reality, it is the process of redesigning how an organisation operates by combining modern technology with improved business processes and organisational change.",
          "Successful digital transformation improves productivity, customer experience, decision-making and operational efficiency while preparing organisations for future growth."
        ]
      },

      {
        heading: "Phase 1 – Assess Current Business Processes",

        paragraphs: [
          "Before selecting any technology, organisations should document existing workflows, identify inefficiencies and understand where delays, duplicate work and manual processes occur.",
          "Technology should solve clearly identified business problems rather than being implemented because it is fashionable."
        ]
      },

      {
        heading: "Phase 2 – Define Business Objectives",

        paragraphs: [
          "Transformation projects should begin with measurable objectives such as reducing operational costs, improving customer satisfaction, increasing productivity or shortening approval cycles.",
          "Clear objectives help prioritise investments and measure project success."
        ]
      },

      {
        heading: "Phase 3 – Select the Right Technology",

        paragraphs: [
          "Choose technology platforms that integrate well with existing systems, support future scalability and provide sufficient flexibility for changing business requirements.",
          "ERP, CRM, AI-powered automation and workflow management platforms should complement each other rather than operate as isolated systems."
        ]
      },

      {
        heading: "Phase 4 – Implementation and Change Management",

        paragraphs: [
          "Technology adoption depends heavily on people. Employees should be involved throughout implementation, trained adequately and supported during organisational change.",
          "Executive sponsorship and clear communication significantly improve user acceptance."
        ]
      },

      {
        heading: "Continuous Improvement",

        paragraphs: [
          "Digital transformation is an ongoing journey rather than a one-time project. Organisations should continuously monitor performance, collect user feedback and identify opportunities for further automation and optimisation."
        ]
      }

    ],

    related: [
      "choosing-the-right-erp-for-your-business",
      "how-ai-can-transform-business-operations",
      "modernising-legacy-business-software"
    ]

  },

  /* =============================================================================
     ARTICLE 4
  ============================================================================= */

  {
    slug: "crm-vs-erp-understanding-the-difference",

    featured: false,

    title: "CRM vs ERP: Understanding the Difference",

    excerpt:
      "CRM and ERP systems solve different business problems. Learn the differences, understand when to implement each solution and discover why many organisations eventually need both.",

    seoTitle:
      "CRM vs ERP | Which Business System Do You Need?",

    seoDescription:
      "Understand the key differences between Customer Relationship Management (CRM) and Enterprise Resource Planning (ERP) software and determine which solution best fits your organisation.",

    category: "Enterprise Software",

    tags: [
      "CRM",
      "ERP",
      "Business Software",
      "Customer Management",
      "Enterprise Systems"
    ],

    readingTime: "8 min read",

    date: "Jul 2026",

    publishedAt: "2026-07-12",

    author: authors.maya,

    cover: "from-[#6667AB] via-[#7575CF] to-[#736AFF]/70",

    body: [

      {
        paragraphs: [
          "Many organisations evaluating enterprise software ask whether they need a CRM system or an ERP system. The answer depends on the business challenges they are trying to solve.",
          "Although both platforms manage business information, they serve different purposes and often complement each other."
        ]
      },

      {
        heading: "What is CRM?",

        paragraphs: [
          "Customer Relationship Management (CRM) software focuses on customers. It helps organisations manage sales opportunities, marketing campaigns, customer communication, support requests and long-term customer relationships.",
          "CRM systems provide sales teams with a complete view of customer interactions and help improve customer engagement."
        ]
      },

      {
        heading: "What is ERP?",

        paragraphs: [
          "Enterprise Resource Planning (ERP) software manages internal business operations including finance, procurement, inventory, manufacturing, human resources, assets and business reporting.",
          "ERP ensures that different departments work from a single source of accurate business information."
        ]
      },

      {
        heading: "When Should You Implement CRM?",

        paragraphs: [
          "If your organisation is struggling to manage leads, customer communication, quotations, follow-ups or sales forecasting, CRM should usually be the first priority."
        ]
      },

      {
        heading: "When Should You Implement ERP?",

        paragraphs: [
          "If operational inefficiencies exist across finance, inventory, procurement or internal processes, ERP generally delivers greater organisational value."
        ]
      },

      {
        heading: "Many Organisations Need Both",

        paragraphs: [
          "As organisations grow, CRM and ERP typically become complementary systems. CRM manages customer-facing activities while ERP manages internal operations. Integration between both platforms provides complete visibility from sales through delivery, invoicing and after-sales support."
        ]
      }

    ],

    related: [
      "choosing-the-right-erp-for-your-business",
      "digital-transformation-roadmap",
      "business-processes-you-should-automate-first"
    ]

  },

  /* =============================================================================
   ARTICLE 5
  ============================================================================= */

  {
    slug: "modernising-legacy-business-software",

    featured: false,

    title: "Modernising Legacy Business Software: When Is the Right Time?",

    excerpt:
      "Legacy software eventually becomes expensive to maintain, difficult to integrate and unable to support business growth. Learn how to recognise when modernisation is the better investment.",

    seoTitle:
      "Modernising Legacy Business Software | VibeWell Software Solutions",

    seoDescription:
      "Discover the warning signs that indicate legacy software should be modernised and explore practical strategies for successful application modernisation.",

    category: "Enterprise Software",

    tags: [
      "Legacy Software",
      "Application Modernisation",
      "Digital Transformation",
      "Software Development",
      "Enterprise Architecture"
    ],

    readingTime: "8 min read",

    date: "Jul 2026",

    publishedAt: "2026-07-08",

    author: authors.maya,

    cover: "from-[#7F38EC] via-[#7F38EC]/80 to-[#9E7BFF]/70",

    body: [

      {
        paragraphs: [
          "Many organisations continue using software developed ten or even twenty years ago. While these systems often continue to function, they gradually become barriers to innovation, security and operational efficiency.",
          "Modernising legacy applications allows organisations to reduce maintenance costs, improve user experience and integrate with modern cloud platforms and business applications."
        ]
      },

      {
        heading: "Common Warning Signs",

        paragraphs: [
          "Frequent system failures, unsupported technologies, poor performance, security vulnerabilities and increasing maintenance costs are clear indicators that software has reached the end of its practical life.",
          "Difficulty finding developers who understand older technologies is another significant business risk."
        ]
      },

      {
        heading: "Modernisation Does Not Always Mean Replacement",

        paragraphs: [
          "Replacing an entire application is not always necessary. Many organisations successfully modernise their systems incrementally by upgrading individual modules, exposing APIs or migrating functionality to cloud-native services.",
          "This phased approach reduces implementation risk while allowing the organisation to continue normal business operations."
        ]
      },

      {
        heading: "Benefits of Modern Platforms",

        paragraphs: [
          "Modern software platforms provide stronger security, improved scalability, mobile accessibility, better reporting, easier integration and lower long-term maintenance costs.",
          "They also provide a better foundation for Artificial Intelligence and workflow automation initiatives."
        ]
      },

      {
        heading: "Planning a Successful Modernisation",

        paragraphs: [
          "Application modernisation should begin with a technical assessment, business process review and long-term roadmap. Prioritising high-value modules first allows organisations to realise measurable benefits while controlling project risk."
        ]
      }

    ],

    related: [
      "digital-transformation-roadmap",
      "choosing-the-right-erp-for-your-business",
      "how-ai-can-transform-business-operations"
    ]

  },

  /* =============================================================================
     ARTICLE 6
  ============================================================================= */

  {
    slug: "business-processes-you-should-automate-first",

    featured: false,

    title: "Business Processes You Should Automate First",

    excerpt:
      "Automation delivers the greatest value when applied to repetitive, time-consuming business activities. Discover which processes typically provide the fastest return on investment.",

    seoTitle:
      "Business Processes You Should Automate First | VibeWell",

    seoDescription:
      "Explore high-impact workflow automation opportunities across HR, Finance, Procurement, Customer Service and Operations.",

    category: "Workflow Automation",

    tags: [
      "Workflow Automation",
      "Business Process",
      "AI",
      "Automation",
      "Enterprise Software"
    ],

    readingTime: "8 min read",

    date: "Jul 2026",

    publishedAt: "2026-07-05",

    author: authors.maya,

    // cover: "from-orange-500 via-primary to-secondary",
    cover: "from-[#B09FCA] via-[#B09FCA]/80 to-[#DCD0FF]/80",

    body: [

      {
        paragraphs: [
          "Every organisation performs hundreds of repetitive business activities every day. Manual approvals, document processing, spreadsheet updates and email-based workflows consume valuable employee time while increasing the likelihood of human error.",
          "Workflow automation eliminates these repetitive tasks and allows employees to focus on work that creates greater business value."
        ]
      },

      {
        heading: "Human Resources",

        paragraphs: [
          "Employee onboarding, leave approvals, attendance management, recruitment workflows and performance review processes are among the most common HR automation opportunities."
        ]
      },

      {
        heading: "Finance and Procurement",

        paragraphs: [
          "Invoice processing, purchase approvals, expense claims, vendor onboarding and payment approvals can all be automated using modern workflow platforms integrated with ERP systems."
        ]
      },

      {
        heading: "Customer Service",

        paragraphs: [
          "Support ticket routing, customer notifications, service escalation, appointment scheduling and knowledge management can all benefit from AI-assisted workflow automation."
        ]
      },

      {
        heading: "Operations",

        paragraphs: [
          "Asset inspections, preventive maintenance scheduling, inventory replenishment, quality inspections and compliance reporting are excellent candidates for automation."
        ]
      },

      {
        heading: "Start Small, Scale Quickly",

        paragraphs: [
          "Successful automation programmes usually begin with one well-defined business process. Once measurable benefits are achieved, organisations can progressively automate additional workflows while maintaining governance and operational control."
        ]
      }

    ],

    related: [
      "how-ai-can-transform-business-operations",
      "digital-transformation-roadmap",
      "crm-vs-erp-understanding-the-difference"
    ]

  }

  // {
  //   id: "choosing-right-erp",
  //   slug: "choosing-the-right-erp",
  //   title: "Choosing the Right ERP for Your Business",
  //   seoTitle: "Choosing the Right ERP for Your Business | VibeWell Software Solutions",
  //   excerpt:
  //     "Selecting the right ERP solution is one of the most important technology decisions an organisation can make. Learn how to evaluate ERP platforms, avoid costly implementation mistakes and maximise return on investment.",
  //   category: "Design",
  //   tags: ["Design Systems", "Tokens", "Scale", "Frontend"],
  //   readingTime: "6 min read",
  //   date: "Dec 2025",
  //   publishedAt: "2025-12-04",
  //   author: authors.maya,
  //   cover: "from-primary/80 via-primary to-secondary",
  //   seoDescription:
  //     "A practical guide to building token-driven design systems that scale with your product and team — without collapsing under their own weight.",
  //   body: [
  //     {
  //       paragraphs: [
  //         "Most design systems don't fail at launch. They fail eighteen months later, when the token names no longer match reality, three teams have forked the button, and nobody trusts the documentation. Scale isn't about having more components — it's about having fewer decisions to re-make.",
  //       ],
  //     },
  //     {
  //       heading: "Start with tokens, not components",
  //       paragraphs: [
  //         "A resilient system begins with a small, opinionated set of design tokens: colour, spacing, radius, typography and motion. Components are just agreements about how those tokens combine. When the foundation is a flat, semantic token layer, rebranding or theming becomes a configuration change rather than a rewrite.",
  //         "The trap is naming tokens after their appearance (blue-500) instead of their intent (color-primary). Intent-based names survive redesigns; appearance-based names guarantee churn.",
  //       ],
  //     },
  //     {
  //       heading: "Make the right thing the easy thing",
  //       paragraphs: [
  //         "Adoption is a distribution problem, not a documentation problem. If using the system is slower than not using it, engineers will route around it. Ship the system as real, typed, importable code — with sensible defaults — so the fastest path is also the correct one.",
  //       ],
  //     },
  //     {
  //       heading: "Govern lightly, evolve constantly",
  //       paragraphs: [
  //         "Treat the system as a living product with an owner, a changelog and a feedback loop. Version it, deprecate gracefully, and measure adoption. A system that can't change is a system teams will eventually abandon.",
  //       ],
  //     },
  //   ],
  //   related: ["performance-is-a-conversion-strategy", "designing-ai-into-product-loops"],
  //   featured: true
  // },
  // {
  //   id: "ai-product-loops",
  //   slug: "designing-ai-into-product-loops",
  //   title: "How AI Can Transform Business Operations",
  //   excerpt:
  //     "Practical patterns for embedding LLMs without breaking user trust.",
  //   category: "AI",
  //   tags: ["AI", "LLM", "Product", "UX"],
  //   readingTime: "8 min read",
  //   date: "Nov 2025",
  //   publishedAt: "2025-11-18",
  //   author: authors.iris,
  //   cover: "from-accent/80 via-accent to-primary",
  //   seoDescription:
  //     "Design patterns for embedding LLMs into products responsibly — keeping humans in control, managing uncertainty and preserving user trust.",
  //   body: [
  //     {
  //       paragraphs: [
  //         "Adding AI to a product is easy. Adding it in a way people trust is the hard part. The difference is rarely the model — it's the design of the loop around it.",
  //       ],
  //     },
  //     {
  //       heading: "Keep the human in the loop",
  //       paragraphs: [
  //         "The most durable AI features draft, they don't decide. Let the model propose — a reply, a summary, a next step — and let the person approve, edit or reject. This preserves agency, creates a natural correction signal, and dramatically reduces the cost of being wrong.",
  //       ],
  //     },
  //     {
  //       heading: "Design for uncertainty",
  //       paragraphs: [
  //         "LLMs are probabilistic. Good AI UX communicates confidence honestly, makes it trivial to undo, and never hides that a suggestion came from a model. Surfacing sources and reasoning turns a black box into a collaborator.",
  //       ],
  //     },
  //     {
  //       heading: "Close the feedback loop",
  //       paragraphs: [
  //         "Every accept, edit and reject is training signal and product insight. Capture it. The teams that win with AI aren't the ones with the best prompts — they're the ones with the tightest loop between usage and improvement.",
  //       ],
  //     },
  //   ],
  //   related: ["design-systems-that-scale", "shipping-in-tight-loops"],
  //   featured: true
  // },
  // {
  //   id: "performance-conversion",
  //   slug: "performance-is-a-conversion-strategy",
  //   title: "Digital Transformation Roadmap for Growing Businesses",
  //   excerpt:
  //     "Why every 100ms matters and the metrics we optimise for on launch.",
  //   category: "Engineering",
  //   tags: ["Performance", "Core Web Vitals", "Conversion", "Frontend"],
  //   readingTime: "5 min read",
  //   date: "Nov 2025",
  //   publishedAt: "2025-11-02",
  //   author: authors.devan,
  //   cover: "from-indigo-500 via-indigo-600 to-secondary",
  //   seoDescription:
  //     "Speed is not a technical nicety — it's a conversion lever. Here are the performance metrics we protect and why every 100ms matters.",
  //   body: [
  //     {
  //       paragraphs: [
  //         "Performance is often filed under 'engineering polish'. That's a mistake. Speed is one of the most reliable conversion levers you have, and it compounds across every visit.",
  //       ],
  //     },
  //     {
  //       heading: "The metrics that matter",
  //       paragraphs: [
  //         "We optimise for the Core Web Vitals — Largest Contentful Paint, Interaction to Next Paint and Cumulative Layout Shift — because they map to what users actually feel: how fast it loads, how fast it responds, and how stable it is while doing so.",
  //       ],
  //     },
  //     {
  //       heading: "Protect speed with a budget",
  //       paragraphs: [
  //         "Performance decays silently as features accumulate. A performance budget — enforced in CI — turns speed from a one-time optimisation into a standing constraint. If a change blows the budget, it doesn't ship until it's fixed.",
  //       ],
  //     },
  //     {
  //       heading: "Fast is a feature",
  //       paragraphs: [
  //         "Users rarely praise a fast site, but they abandon a slow one. Treating speed as a first-class feature — measured, owned and defended — is one of the highest-leverage things a team can do.",
  //       ],
  //     },
  //   ],
  //   related: ["design-systems-that-scale", "shipping-in-tight-loops"],
  //   featured: true
  // },
  // {
  //   id: "shipping-in-tight",
  //   slug: "shipping-in-tight-loops",
  //   title: "CRM vs ERP",
  //   excerpt:
  //     "How small, transparent delivery cycles de-risk even the biggest builds.",
  //   category: "Engineering",
  //   tags: ["Delivery", "Process", "Testing", "Agile"],
  //   readingTime: "6 min read",
  //   date: "Oct 2025",
  //   publishedAt: "2025-10-20",
  //   author: authors.devan,
  //   cover: "from-emerald-500 via-primary to-secondary",
  //   seoDescription:
  //     "Why tight, tested delivery loops beat big-bang releases — and how transparent cycles de-risk ambitious software builds.",
  //   body: [
  //     {
  //       paragraphs: [
  //         "The riskiest way to build software is to disappear for six months and reappear with a finished product. Reality has a way of invalidating assumptions, and the longer the loop, the more expensive the surprise.",
  //       ],
  //     },
  //     {
  //       heading: "Small batches, visible progress",
  //       paragraphs: [
  //         "Shipping in tight loops means every increment is demonstrable, tested and reversible. Stakeholders see progress continuously, feedback arrives while it's still cheap to act on, and momentum stays high.",
  //       ],
  //     },
  //     {
  //       heading: "Testing is part of the loop",
  //       paragraphs: [
  //         "Speed without rigour is just debt in disguise. Automated tests, previews and monitoring let us move fast precisely because we can trust each step. The loop isn't 'build then test' — it's 'build with tests'.",
  //       ],
  //     },
  //   ],
  //   related: ["performance-is-a-conversion-strategy", "designing-ai-into-product-loops"],
  //   featured: false
  // },
  // {
  //   id: "accessibility-is-good",
  //   slug: "accessibility-is-good-business",
  //   title: "When Should You Replace Legacy Software?",
  //   excerpt:
  //     "WCAG 2.2 AA isn't a checkbox — it's a wider audience and a safer brand.",
  //   category: "Design",
  //   tags: ["Accessibility", "WCAG", "Inclusive Design", "UX"],
  //   readingTime: "5 min read",
  //   date: "Oct 2025",
  //   publishedAt: "2025-10-06",
  //   author: authors.maya,
  //   cover: "from-secondary via-primary to-accent",
  //   seoDescription:
  //     "Accessibility expands your audience, strengthens your brand and reduces risk. Why we build to WCAG 2.2 AA from day one.",
  //   body: [
  //     {
  //       paragraphs: [
  //         "Accessibility is too often treated as a compliance chore bolted on at the end. Framed that way, it's expensive and joyless. Framed as inclusive design from day one, it's simply better product work.",
  //       ],
  //     },
  //     {
  //       heading: "A wider audience by default",
  //       paragraphs: [
  //         "Roughly one in five people has a disability. Accessible design also helps everyone in imperfect conditions — bright sunlight, a noisy train, a slow connection. Building to WCAG 2.2 AA widens your reachable market at no extra cost when it's designed in early.",
  //       ],
  //     },
  //     {
  //       heading: "Better for everyone",
  //       paragraphs: [
  //         "Clear contrast, keyboard support, meaningful structure and honest labels make interfaces easier for all users — not just those relying on assistive technology. Accessibility and usability are the same pursuit from different angles.",
  //       ],
  //     },
  //   ],
  //   related: ["design-systems-that-scale", "performance-is-a-conversion-strategy"],
  //   featured: false
  // },
  // {
  //   id: "choosing-tech-stack",
  //   slug: "choosing-a-tech-stack-that-lasts",
  //   title: "Workflow Automation Opportunities Every Business Should Explore",
  //   excerpt:
  //     "How we pick tools for reliability and speed — not hype.",
  //   category: "Engineering",
  //   tags: ["Architecture", "Tech Stack", "Decisions", "Scale"],
  //   readingTime: "7 min read",
  //   date: "Sep 2025",
  //   publishedAt: "2025-09-22",
  //   author: authors.iris,
  //   cover: "from-primary via-secondary to-indigo-600",
  //   seoDescription:
  //     "A framework for choosing a technology stack that lasts — optimising for reliability, hiring and total cost over hype.",
  //   body: [
  //     {
  //       paragraphs: [
  //         "Every stack decision is a bet on the future — on hiring, on maintenance, on how the product will grow. Chasing the newest framework is a great way to lose that bet.",
  //       ],
  //     },
  //     {
  //       heading: "Optimise for boring where it counts",
  //       paragraphs: [
  //         "For the load-bearing parts of a system, proven and boring beats novel and exciting. Mature tools have known failure modes, deep ecosystems and large hiring pools. Save your innovation budget for the places that actually differentiate your product.",
  //       ],
  //     },
  //     {
  //       heading: "Decide with a scorecard, not a vibe",
  //       paragraphs: [
  //         "We weigh each choice on reliability, performance, ecosystem, hiring and total cost of ownership. Writing the reasoning down turns a gut call into a decision the whole team can revisit and trust.",
  //       ],
  //     },
  //   ],
  //   related: ["shipping-in-tight-loops", "performance-is-a-conversion-strategy"],
  //   featured: false
  // },
];

const bySlug = new Map(articles.map((a) => [a.slug, a]));

export function getArticle(slug?: string): Article | undefined {
  return slug ? bySlug.get(slug) : undefined;
}

export function getRelatedArticles(slug: string): Article[] {
  const article = bySlug.get(slug);
  if (!article) return [];
  return article.related
    .map((s) => bySlug.get(s))
    .filter((a): a is Article => Boolean(a));
}

export function articleCategories(): {
  name: string;
  count: number;
}[] {

  const counts = new Map<string, number>();

  for (const article of articles) {

    counts.set(
      article.category,
      (counts.get(article.category) ?? 0) + 1
    );

  }

  return Array.from(counts.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([name, count]) => ({
      name,
      count,
    }));

}

/** Unique category list (derived) with post counts. */
// export function articleCategories(): { name: string; count: number }[] {
//   const counts = new Map<string, number>();
//   for (const a of articles) counts.set(a.category, (counts.get(a.category) ?? 0) + 1);
//   return Array.from(counts.entries()).map(([name, count]) => ({ name, count }));
// }
